import React, { useEffect, useMemo, useState } from "react";
import { deleteAdminSubmittedQuestion, getAdminSubmittedQuestions } from "../../../api/questions";
import { useNotification } from "../../../context/NotificationContext";
import { DashboardReloadButton } from "./dashboardUi";

const pick = (row, ...keys) => {
  for (const k of keys) {
    if (row && row[k] != null && row[k] !== "") return row[k];
  }
  return "";
};

const SubmittedQuestionsManager = () => {
  const { notify } = useNotification();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await getAdminSubmittedQuestions();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({
        type: "error",
        title: "Failed to load submitted questions",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const sortedItems = useMemo(
    () =>
      [...items].sort(
        (a, b) => new Date(pick(b, "createdAtUtc", "CreatedAtUtc") || 0).getTime() -
          new Date(pick(a, "createdAtUtc", "CreatedAtUtc") || 0).getTime()
      ),
    [items]
  );

  const handleDelete = async (item) => {
    const id = pick(item, "id", "Id");
    const ok = window.confirm(`Delete question submission #${id}?`);
    if (!ok) return;
    try {
      await deleteAdminSubmittedQuestion(id);
      notify({ type: "success", title: "Question submission deleted" });
      await loadItems();
    } catch (error) {
      notify({
        type: "error",
        title: "Delete failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex justify-end">
        <DashboardReloadButton onClick={loadItems} loading={loading} />
      </div>

      <div className="dashboard-table-wrap dashboard-h-scroll-light overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full table-fixed">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="w-[6%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">ID</th>
              <th className="w-[11%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
              <th className="w-[14%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Email</th>
              <th className="w-[11%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Phone</th>
              <th className="w-[10%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Location</th>
              <th className="w-[32%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Question</th>
              <th className="w-[10%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Created</th>
              <th className="w-[6%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="px-3 py-4 text-sm text-slate-500">
                  Loading submitted questions...
                </td>
              </tr>
            ) : sortedItems.length ? (
              sortedItems.map((item) => {
                const id = pick(item, "id", "Id");
                const q = String(pick(item, "questionText", "QuestionText") || "");
                const preview = q.length > 160 ? `${q.slice(0, 160)}…` : q;
                const created = pick(item, "createdAtUtc", "CreatedAtUtc");
                return (
                  <tr key={id} className="border-b border-slate-200 align-top">
                    <td className="px-3 py-3 text-sm text-slate-700">{id}</td>
                    <td className="px-3 py-3 text-sm text-slate-900 font-medium">{pick(item, "name", "Name") || "-"}</td>
                    <td className="px-3 py-3 text-sm text-slate-700 break-all">{pick(item, "email", "Email") || "-"}</td>
                    <td className="px-3 py-3 text-sm text-slate-700 break-all">{pick(item, "phoneNumber", "PhoneNumber") || "-"}</td>
                    <td className="px-3 py-3 text-sm text-slate-700">{pick(item, "location", "Location") || "-"}</td>
                    <td className="px-3 py-3 text-sm text-slate-700">
                      <span className="line-clamp-4" title={q || undefined}>
                        {preview || "-"}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-sm text-slate-700">
                      {created ? new Date(created).toLocaleString() : "-"}
                    </td>
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => handleDelete(item)}
                        className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={8} className="px-3 py-4 text-sm text-slate-500">
                  No submitted questions yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default SubmittedQuestionsManager;
